import { useAuthStore } from '@/auth/authStore';
import { SERVER_URL, COMMON_HEADERS } from '@/config/serverUrl';
import type { GlobalMessage } from '@/social/socialApi';

export type ClanRole = 'leader' | 'coLeader' | 'elder' | 'member';
export type ClanJoinPolicy = 'open' | 'request' | 'invite';

export interface ClanMemberDTO {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  avatarColor: 'red' | 'blue';
  role: ClanRole;
  rank: string;
  mmr: number;
  warPoints: number;
  joinedAt: number;
}

export interface ClanSummaryDTO {
  id: string;
  name: string;
  tag: string;
  emblem: string | null;
  language: string | null;
  description: string | null;
  joinPolicy: ClanJoinPolicy;
  minMmr: number;
  memberCount: number;
  maxMembers: number;
  trophies: number;
}

export interface ClanJoinRequestDTO {
  userId: string;
  displayName: string;
  mmr: number;
  message: string | null;
  at: number;
}

export interface ClanDetailDTO extends ClanSummaryDTO {
  leaderId: string;
  members: ClanMemberDTO[];
  joinRequests?: ClanJoinRequestDTO[];
  myRole: ClanRole | null;
}

export interface CreateRequirements {
  minLevel: number;
  costCoins: number;
  costGems: number;
  level: number;
  coins: number;
  gems: number;
  inClan: boolean;
  canCreate: boolean;
}

export interface ClanEmblemItem {
  id: string;
  name: string;
  imageUrl: string;
  rarity: string;
  priceGems: number;
  special: boolean;
  owned: boolean;
}

export interface CreateClanInput {
  name: string;
  tag: string;
  description?: string;
  language?: string;
  joinPolicy?: ClanJoinPolicy;
  emblem?: string;
}

export interface UpdateClanInput {
  description?: string;
  joinPolicy?: ClanJoinPolicy;
  emblem?: string;
  language?: string;
  minMmr?: number;
}

export interface WarContributorDTO {
  userId: string;
  displayName: string;
  points: number;
  wins: number;
}

export interface WarSideDTO {
  clanId: string;
  name: string;
  tag: string;
  emblem: string | null;
  trophies: number;
  score: number;
  contributors: WarContributorDTO[];
}

export interface ActiveWarDTO {
  id: string;
  state: 'prep' | 'battle' | 'ended';
  prepStartAt: number;
  battleStartAt: number;
  endAt: number;
  you: WarSideDTO;
  rival: WarSideDTO;
  attacksPerMember: number;
  myAttacksUsed: number;
  canAttack: boolean;
  winnerClanId: string | null;
}

export interface ClanSeasonDTO {
  number: number;
  startedAt: number;
  endsAt: number;
}

export interface WarRegStatus {
  registered: boolean;
  windowOpen: boolean;
  canRegister: boolean;
  regOpensAt: number;
  regClosesAt: number;
  battleStartAt: number;
  battleEndAt: number;
  minMembers: number;
  memberCount: number;
}

export type ClanLeaderboardEntry = ClanSummaryDTO & { rankIndex: number };

export type ClanNotifType =
  | 'join_request'
  | 'join_approved'
  | 'join_rejected'
  | 'kicked'
  | 'role_changed'
  | 'war_scheduled'
  | 'war_open'
  | 'war_ended'
  | 'emblem_awarded';

export interface ClanNotifDTO {
  id: string;
  type: ClanNotifType;
  data: Record<string, unknown>;
  /** ms epoch when read, or null if still unread. */
  readAt: number | null;
  createdAt: number;
}

const BASE = SERVER_URL;

/** Thrown so callers can branch on the server's machine code (e.g. CLAN_FULL). */
export class ClanApiError extends Error {
  constructor(public code: string, message: string) {
    super(message);
  }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...COMMON_HEADERS,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body == null ? undefined : JSON.stringify(body),
    credentials: 'include',
  });
  if (!res.ok) {
    let code = `HTTP_${res.status}`;
    let message = '';
    try {
      const j = await res.json();
      code = j?.error ?? code;
      message = j?.message ?? '';
    } catch { /* */ }
    throw new ClanApiError(code, message || `Clan API ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const clanApi = {
  status: () => req<{ enabled: boolean }>('GET', '/clans/status'),
  me: () => req<{ clan: ClanDetailDTO | null; pendingRequestId: string | null }>('GET', '/clans/me'),
  war: () => req<{ war: ActiveWarDTO | null; reg: WarRegStatus | null }>('GET', '/clans/war'),
  registerWar: (id: string) => req<{ ok: true }>('POST', `/clans/${id}/war/register`),
  leaderboard: () =>
    req<{ items: ClanLeaderboardEntry[]; season: ClanSeasonDTO }>('GET', '/clans/leaderboard'),
  browse: (query?: string, language?: string) => {
    const p = new URLSearchParams();
    if (query) p.set('query', query);
    if (language) p.set('language', language);
    const qs = p.toString();
    return req<{ items: ClanSummaryDTO[] }>('GET', `/clans/browse${qs ? `?${qs}` : ''}`);
  },
  detail: (id: string) => req<{ clan: ClanDetailDTO }>('GET', `/clans/${id}`),
  createRequirements: () => req<CreateRequirements>('GET', '/clans/create-requirements'),
  create: (input: CreateClanInput) => req<{ clan: ClanDetailDTO }>('POST', '/clans', input),
  join: (id: string, message?: string) =>
    req<{ joined: boolean }>('POST', `/clans/${id}/join`, message ? { message } : undefined),
  cancelJoin: (id: string) => req<{ ok: true }>('DELETE', `/clans/${id}/join`),
  approve: (id: string, userId: string) =>
    req<{ ok: true }>('POST', `/clans/${id}/requests/${userId}/approve`),
  reject: (id: string, userId: string) =>
    req<{ ok: true }>('POST', `/clans/${id}/requests/${userId}/reject`),
  leave: () => req<{ ok: true }>('POST', '/clans/leave'),
  kick: (id: string, userId: string) => req<{ ok: true }>('POST', `/clans/${id}/kick/${userId}`),
  setRole: (id: string, userId: string, role: Exclude<ClanRole, 'leader'>) =>
    req<{ ok: true }>('POST', `/clans/${id}/role/${userId}`, { role }),
  transfer: (id: string, userId: string) =>
    req<{ ok: true }>('POST', `/clans/${id}/transfer/${userId}`),
  update: (id: string, patch: UpdateClanInput) =>
    req<{ clan: ClanDetailDTO }>('PATCH', `/clans/${id}`, patch),
  disband: (id: string) => req<{ ok: true }>('DELETE', `/clans/${id}`),
  notifications: () => req<{ items: ClanNotifDTO[]; unread: number }>('GET', '/clans/notifications'),
  markNotificationsRead: () => req<{ marked: number }>('POST', '/clans/notifications/read'),
  chatHistory: () => req<{ messages: GlobalMessage[] }>('GET', '/clans/chat'),
  emblems: (id: string) =>
    req<{ equipped: string | null; items: ClanEmblemItem[] }>('GET', `/clans/${id}/emblems`),
  buyEmblem: (id: string, emblemId: string) =>
    req<{ gems: number }>('POST', `/clans/${id}/emblems/${emblemId}/buy`),
  equipEmblem: (id: string, emblemId: string) =>
    req<{ ok: true }>('POST', `/clans/${id}/emblems/${emblemId}/equip`),
};
